/**
 * <arcero-hero-slideshow> — autoplaying hero carousel for the homepage.
 * Direct port of the mockup's hero JS (forside-mockup.html, ~L671-724):
 * timed autoplay with CSS-driven progress bars, pause on hover/focus,
 * click-to-jump bars, arrow nav, keyboard arrows, touch swipe.
 *
 * Bars are built here, not in Liquid, because nested child-blocks rendered
 * via content_for 'blocks' have no forloop index to key a bar off of — see
 * the doc comment in sections/arcero-hero-slideshow.liquid.
 */
class ArceroHeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.getAttribute('autoplay') !== 'false';
    this.duration = Number(this.getAttribute('autoplay-speed')) || 7000;

    this.slides = Array.from(this.querySelectorAll('[data-arcero-hero-slide]'));
    this.barsContainer = this.querySelector('[data-arcero-hero-bars]');
    this.indexEl = this.querySelector('[data-arcero-hero-index]');
    this.collectionLabelEl = this.querySelector('[data-arcero-hero-collection-label]');
    this.prevButton = this.querySelector('[data-arcero-hero-prev]');
    this.nextButton = this.querySelector('[data-arcero-hero-next]');

    if (this.slides.length === 0) return;

    if (this.barsContainer) {
      this.slides.forEach((slide, i) => {
        const bar = document.createElement('button');
        bar.type = 'button';
        bar.className = 'arcero-hero-slideshow__bar';
        bar.setAttribute('role', 'tab');
        bar.setAttribute('aria-label', `Slide ${i + 1}`);
        bar.addEventListener('click', () => this.go(i));
        this.barsContainer.appendChild(bar);
      });
    }
    this.barEls = this.barsContainer ? Array.from(this.barsContainer.children) : [];

    this.current = 0;
    this.timer = null;

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.go(this.current - 1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.go(this.current + 1));
    }

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.go(this.current + 1);
      if (event.key === 'ArrowLeft') this.go(this.current - 1);
    });

    this.addEventListener('mouseenter', () => this.clearTimer());
    this.addEventListener('mouseleave', () => this.restart());
    this.addEventListener('focusin', () => this.clearTimer());
    this.addEventListener('focusout', () => this.restart());

    let swipeStartX = null;
    this.addEventListener('touchstart', (event) => {
      swipeStartX = event.touches[0].clientX;
    }, { passive: true });
    this.addEventListener('touchend', (event) => {
      if (swipeStartX === null) return;
      const delta = event.changedTouches[0].clientX - swipeStartX;
      swipeStartX = null;
      if (Math.abs(delta) > 55) this.go(this.current + (delta < 0 ? 1 : -1));
    });

    this.paint();
    this.restart();
  }

  go(index) {
    this.current = (index + this.slides.length) % this.slides.length;
    this.paint();
    this.restart();
  }

  clearTimer() {
    clearTimeout(this.timer);
  }

  restart() {
    this.clearTimer();
    if (this.autoplay && !this.reduceMotion && this.slides.length > 1) {
      this.timer = setTimeout(() => this.go(this.current + 1), this.duration);
    }
  }

  paint() {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-on', i === this.current);
    });

    const activeSlide = this.slides[this.current];

    if (this.indexEl) {
      this.indexEl.textContent = `${pad(this.current + 1)} / ${pad(this.slides.length)}`;
    }
    if (this.collectionLabelEl) {
      this.collectionLabelEl.textContent = activeSlide.dataset.collectionLabel || '';
    }

    this.barEls.forEach((bar, i) => {
      bar.classList.remove('is-live', 'is-done');
      if (i < this.current) bar.classList.add('is-done');
    });

    if (this.barEls.length === 0) return;

    if (this.reduceMotion) {
      this.barEls[this.current].classList.add('is-done');
      return;
    }

    const liveBar = this.barEls[this.current];
    liveBar.style.setProperty('--arcero-hero-bar-duration', `${this.duration}ms`);
    // Force reflow so the fill animation restarts from 0 on repeat slides.
    void liveBar.offsetWidth;
    liveBar.classList.add('is-live');
  }
}

function pad(n) {
  return (n < 10 ? '0' : '') + n;
}

customElements.define('arcero-hero-slideshow', ArceroHeroSlideshow);
